<p>Generate a report for winner payouts and/or charity payouts with customizable filters</p>

<!-- Collapsed Filter Summary -->
@if (filtersCollapsed) {
  <mat-card appearance="outlined" class="filter-summary-card" data-testid="payout-report-filter-summary">
    <mat-card-content class="filter-summary-content">
      <div class="filter-summary-text">
        <mat-icon>filter_list</mat-icon>
        <span class="summary-label">Active Filters:</span>
        <span class="summary-values">{{ filterSummary }}</span>
      </div>
      <button mat-raised-button color="primary" (click)="toggleFilters()" data-testid="payout-report-edit-filters-button">
        <mat-icon>edit</mat-icon>
        Edit Filters
      </button>
    </mat-card-content>
  </mat-card>
}

<!-- Expanded Filter Form -->
@if (!filtersCollapsed) {
<form [formGroup]="payoutForm" class="filter-form">
  <!-- Payout Type Selection -->
  <div class="margin-bottom-med">
    <h5>Payout Types</h5>
    <mat-checkbox formControlName="includeWinnerPayouts" data-testid="payout-report-include-winner-checkbox">
      Include Winner Payouts
    </mat-checkbox>
    <mat-checkbox formControlName="includeCharityPayouts" class="margin-left-med" data-testid="payout-report-include-charity-checkbox">
      Include Charity Payouts
    </mat-checkbox>
  </div>

  <!-- Date Range Type Selection -->
  <div class="margin-bottom-med">
    <h5>Date Selection</h5>
    <mat-radio-group formControlName="dateRangeType" data-testid="payout-report-date-range-type">
      <mat-radio-button value="range" class="margin-right-med">Date Range</mat-radio-button>
      <mat-radio-button value="single">Single Date</mat-radio-button>
    </mat-radio-group>
  </div>

  <!-- Date Picker -->
  @if (payoutForm.value.dateRangeType === 'range') {
    <mat-form-field class="full-width">
      <mat-label>Enter a date range</mat-label>
      <mat-date-range-input [rangePicker]="rangePicker">
        <input matStartDate placeholder="Start date" formControlName="startDate" data-testid="payout-report-start-date-input">
        <input matEndDate placeholder="End date" formControlName="endDate" data-testid="payout-report-end-date-input">
      </mat-date-range-input>
      <mat-datepicker-toggle matIconSuffix [for]="rangePicker" data-testid="payout-report-datepicker-toggle"></mat-datepicker-toggle>
      <mat-date-range-picker #rangePicker></mat-date-range-picker>
      <mat-error>Date range is required</mat-error>
    </mat-form-field>
  } @else {
    <mat-form-field class="full-width">
      <mat-label>Choose a date</mat-label>
      <input matInput [matDatepicker]="singlePicker" formControlName="startDate" placeholder="MM/DD/YYYY" data-testid="payout-report-single-date-input">
      <mat-hint>MM/DD/YYYY</mat-hint>
      <mat-datepicker-toggle matSuffix [for]="singlePicker" data-testid="payout-report-single-datepicker-toggle"></mat-datepicker-toggle>
      <mat-datepicker #singlePicker></mat-datepicker>
      <mat-error>Date is required</mat-error>
    </mat-form-field>
  }

  <!-- Charity Payout Filters -->
  @if (payoutForm.value.includeCharityPayouts) {
    <div class="margin-bottom-med margin-top-med">
      <h5>Charity Payout Filters</h5>
      <mat-form-field class="full-width">
        <mat-label>Voided Status</mat-label>
        <mat-select formControlName="charityPayoutVoidedFilter" data-testid="payout-report-charity-voided-filter">
          <mat-option value="not_voided">Not Voided Only</mat-option>
          <mat-option value="voided">Voided Only</mat-option>
          <mat-option value="all">All</mat-option>
        </mat-select>
      </mat-form-field>
    </div>
  }

  <!-- Winner Payout Status Filters -->
  @if (payoutForm.value.includeWinnerPayouts) {
    <div class="margin-bottom-med margin-top-med">
      <h5>Winner Payout Status Filters</h5>
      <mat-checkbox formControlName="winnerPayoutStatusesNotPaid" data-testid="payout-report-status-not-paid">
        Not Paid
      </mat-checkbox>
      <mat-checkbox formControlName="winnerPayoutStatusesPaid" class="margin-left-med" data-testid="payout-report-status-paid">
        Paid
      </mat-checkbox>
      <mat-checkbox formControlName="winnerPayoutStatusesForfeited" class="margin-left-med" data-testid="payout-report-status-forfeited">
        Forfeited
      </mat-checkbox>
      <mat-checkbox formControlName="winnerPayoutStatusesExpired" class="margin-left-med" data-testid="payout-report-status-expired">
        Expired
      </mat-checkbox>
    </div>

    <!-- Date Filter Type for Winner Payouts -->
    <div class="margin-bottom-med">
      <h5>Date Filter Type (Winner Payouts)</h5>
      <mat-radio-group formControlName="dateFilterType" data-testid="payout-report-date-filter-type">
        <mat-radio-button [value]="1" class="margin-right-med">Draw Date</mat-radio-button>
        <mat-radio-button [value]="2">Payout Date</mat-radio-button>
      </mat-radio-group>
    </div>
  }
</form>
}

<div class="d-flex flex-column gap-3 margin-top-med" #resultsSection>
  <div class="d-flex justify-content-end gap-2">
    @if (!filtersCollapsed) {
      <button mat-stroked-button (click)="toggleFilters()" data-testid="payout-report-hide-filters-button">
        <mat-icon>visibility_off</mat-icon>
        Hide Filters
      </button>
    }
    <button mat-raised-button color="primary" (click)="onSubmit()" [disabled]="loading" data-testid="payout-report-generate-button">
      @if (loading) {
        <mat-spinner diameter="20"></mat-spinner>
      } @else {
        Generate Report
      }
    </button>
  </div>

  @if (showReports && reportData) {
    <!-- Summary Cards -->
    <div class="summary-section" data-testid="payout-report-summary">
      <div class="d-flex justify-content-between align-items-center margin-bottom-small">
        <h4>Report Summary</h4>
        <div class="d-flex gap-2">
          <button mat-raised-button color="accent" (click)="generatePDF()" data-testid="payout-report-generate-pdf-button">
            <mat-icon>picture_as_pdf</mat-icon>
            Generate PDF
          </button>
          <button mat-raised-button color="primary" (click)="downloadAllCSV()" data-testid="payout-report-download-all-csv-button">
            <mat-icon>download</mat-icon>
            Download All CSV
          </button>
        </div>
      </div>
      <div class="summary-cards">
        <!-- Charity Payouts Summary -->
        @if (payoutForm.value.includeCharityPayouts) {
          <mat-card appearance="outlined" class="summary-card charity-summary" data-testid="charity-payout-summary">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>volunteer_activism</mat-icon>
                Charity Payouts
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="summary-stat">
                <span class="stat-label">Total Amount:</span>
                <span class="stat-value">{{ charityPayoutTotal | formatCurrency }}</span>
              </div>
              <div class="summary-stat">
                <span class="stat-label">Number of Payouts:</span>
                <span class="stat-value">{{ charityPayoutCount }}</span>
              </div>
            </mat-card-content>
          </mat-card>
        }

        <!-- Winner Payouts Summary -->
        @if (payoutForm.value.includeWinnerPayouts) {
          <mat-card appearance="outlined" class="summary-card winner-summary" data-testid="winner-payout-summary">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>emoji_events</mat-icon>
                Winner Payouts
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="summary-stat">
                <span class="stat-label">Total Amount:</span>
                <span class="stat-value">{{ winnerPayoutTotal | formatCurrency }}</span>
              </div>
              <div class="summary-stat">
                <span class="stat-label">Number of Payouts:</span>
                <span class="stat-value">{{ winnerPayoutCount }}</span>
              </div>
            </mat-card-content>
          </mat-card>
        }

        <!-- Grand Total Summary -->
        <mat-card appearance="outlined" class="summary-card grand-total-summary" data-testid="grand-total-summary">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>account_balance</mat-icon>
              Grand Total
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="summary-stat">
              <span class="stat-label">Total Amount:</span>
              <span class="stat-value grand-total">{{ grandTotalPayouts | formatCurrency }}</span>
            </div>
            <div class="summary-stat">
              <span class="stat-label">Total Payouts:</span>
              <span class="stat-value grand-total">{{ grandTotalCount }}</span>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>

    <mat-tab-group dynamicHeight disableRipple data-testid="payout-report-tab-group">
      <!-- Winner Payouts Tab -->
      @if (payoutForm.value.includeWinnerPayouts && reportData.WinnerPayouts.length > 0) {
        <mat-tab label="Winner Payouts ({{ reportData.WinnerPayouts.length }})" data-testid="payout-report-winner-tab">
          <div class="margin-top-med">
            <table mat-table [dataSource]="reportData.WinnerPayouts" class="full-width" data-testid="payout-report-winner-table">

              <!-- Player Name Column -->
              <ng-container matColumnDef="playerName">
                <th mat-header-cell *matHeaderCellDef>Player Name</th>
                <td mat-cell *matCellDef="let payout">{{ payout.PlayerFirstName }} {{ payout.PlayerLastName }}</td>
              </ng-container>

              <!-- Player Email Column -->
              <ng-container matColumnDef="playerEmail">
                <th mat-header-cell *matHeaderCellDef>Email</th>
                <td mat-cell *matCellDef="let payout">{{ payout.PlayerEmail }}</td>
              </ng-container>

              <!-- Ticket Number Column -->
              <ng-container matColumnDef="ticketNumber">
                <th mat-header-cell *matHeaderCellDef>Ticket #</th>
                <td mat-cell *matCellDef="let payout">{{ payout.TicketNumber }}</td>
              </ng-container>

              <!-- Payout Amount Column -->
              <ng-container matColumnDef="payoutAmount">
                <th mat-header-cell *matHeaderCellDef>Payout Amount</th>
                <td mat-cell *matCellDef="let payout">{{ payout.PayoutAmount | formatCurrency }}</td>
              </ng-container>

              <!-- Status Column -->
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let payout">{{ payout.PayoutStatus | winnerPayoutStatus }}</td>
              </ng-container>

              <!-- Paid Out Date Column -->
              <ng-container matColumnDef="paidOutOnDate">
                <th mat-header-cell *matHeaderCellDef>Paid Out Date</th>
                <td mat-cell *matCellDef="let payout">{{ payout.PaidOutOnDate ? (payout.PaidOutOnDate | date) : 'N/A' }}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="winnerPayoutColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: winnerPayoutColumns;"></tr>
            </table>
          </div>
        </mat-tab>
      }

      <!-- Charity Payouts Tab -->
      @if (payoutForm.value.includeCharityPayouts && reportData.CharityPayouts.length > 0) {
        <mat-tab label="Charity Payouts ({{ reportData.CharityPayouts.length }})" data-testid="payout-report-charity-tab">
          <div class="margin-top-med">
            <table mat-table [dataSource]="reportData.CharityPayouts" class="full-width" data-testid="payout-report-charity-table">

              <!-- Charity Name Column -->
              <ng-container matColumnDef="charityName">
                <th mat-header-cell *matHeaderCellDef>Charity Name</th>
                <td mat-cell *matCellDef="let payout">{{ payout.CharityName }}</td>
              </ng-container>

              <!-- Payout Amount Column -->
              <ng-container matColumnDef="payoutAmount">
                <th mat-header-cell *matHeaderCellDef>Payout Amount</th>
                <td mat-cell *matCellDef="let payout">{{ payout.PayoutAmount | formatCurrency }}</td>
              </ng-container>

              <!-- Created On Date Column -->
              <ng-container matColumnDef="createdOnDate">
                <th mat-header-cell *matHeaderCellDef>Created On</th>
                <td mat-cell *matCellDef="let payout">{{ payout.CreatedOnDate | date }}</td>
              </ng-container>

              <!-- Voided Column -->
              <ng-container matColumnDef="voided">
                <th mat-header-cell *matHeaderCellDef>Voided</th>
                <td mat-cell *matCellDef="let payout">{{ payout.Voided ? 'Yes' : 'No' }}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="charityPayoutColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: charityPayoutColumns;"></tr>
            </table>
          </div>
        </mat-tab>
      }
    </mat-tab-group>

    <!-- No Results Message -->
    @if ((!payoutForm.value.includeWinnerPayouts || reportData.WinnerPayouts.length === 0) &&
         (!payoutForm.value.includeCharityPayouts || reportData.CharityPayouts.length === 0)) {
      <div class="margin-top-med">
        <p>No payouts found matching the selected criteria.</p>
      </div>
    }
  }
</div>
